import { z } from "zod";

/**
 * What a Plan Audit upload will accept.
 *
 * Kept out of `plan-audit.ts` (the run-payload and list-filter contract) because
 * this is about bytes arriving, not about what a run wrote.
 *
 * Its own allowlist rather than the shared `ALLOWED_UPLOAD_TYPES` in
 * `schemas/files.ts`, which says so itself: "a real feature should narrow
 * ALLOWED_UPLOAD_TYPES to exactly what that resource accepts rather than reusing
 * this broad set." A plan is a document; letting a PNG through here would put an
 * unreadable file in front of the audit engine and surface as a confusing
 * failure much later.
 */

/** A Solution Plan, as exported from the Solution tool. */
export const PLAN_UPLOAD_TYPES = [
  "text/markdown",
  "text/plain",
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
] as const;

/**
 * Intake: the same document types, plus subtitle tracks — a recorded intake call
 * arrives as `.vtt` more often than as anything else.
 */
export const INTAKE_UPLOAD_TYPES = [...PLAN_UPLOAD_TYPES, "text/vtt"] as const;

/** Hard cap per file (25 MB), matching the shared generic upload cap. */
export const MAX_PLAN_UPLOAD_BYTES = 25 * 1024 * 1024;

/**
 * Cap on intake files in one submission. Not a storage limit — it bounds the
 * per-file round trips the browser makes before the run is created, and a real
 * intake has never needed twenty documents.
 */
export const MAX_INTAKE_FILES = 20;

export const UPLOAD_ROLES = ["plan", "intake"] as const;
export type UploadRole = (typeof UPLOAD_ROLES)[number];

/** The types a given role accepts. */
export function allowedTypesFor(role: UploadRole): readonly string[] {
  return role === "plan" ? PLAN_UPLOAD_TYPES : INTAKE_UPLOAD_TYPES;
}

/**
 * Body for `POST /api/plan-files` — ask for a presigned PUT URL.
 *
 * The content type is checked against the allowlist **for that role**, so a
 * `.vtt` can be intake but never the plan itself. Browsers routinely report an
 * empty type for `.md` and `.vtt`, so the client maps by file extension before
 * calling this — the server must not accept a blank type and quietly store an
 * unknown file.
 */
export const planFilePresignSchema = z
  .object({
    role: z.enum(UPLOAD_ROLES),
    filename: z.string().min(1, "filename is required").max(255),
    contentType: z.string().min(1, "contentType is required"),
    sizeBytes: z
      .number()
      .int()
      .positive()
      .max(MAX_PLAN_UPLOAD_BYTES, "That file is larger than 25 MB."),
  })
  .superRefine((value, ctx) => {
    if (!allowedTypesFor(value.role).includes(value.contentType)) {
      ctx.addIssue({
        code: "custom",
        path: ["contentType"],
        message:
          value.role === "plan"
            ? "A Solution Plan must be Markdown, plain text, PDF or Word."
            : "Intake files must be Markdown, plain text, PDF, Word or WebVTT.",
      });
    }
  });
